/** Parse a property link without fetching it or trusting URL lookalike hosts. */
export const ZILLOW_URL_ERROR =
  "Enter a Zillow property link like https://www.zillow.com/homedetails/123_zpid/.";

/** @param {string} value @returns {boolean} */
export const looksLikeListingUrl = (value) =>
  /^(?:[a-z][a-z0-9+.-]*:|\/\/|www\.|(?:m\.)?zillow\.com(?:[/:?#]|$)|[^\s/]+\.[a-z]{2,}(?:[/:?#]|$))/i.test(
    value.trim()
  );

/**
 * Tracking query/fragment and address slug are discarded: the numeric ZPID
 * identifies the property. Bare Zillow hosts are accepted for pasted links;
 * explicit insecure schemes, credentials and ports are never accepted.
 * @param {unknown} value
 * @returns {string | null}
 */
export const normalizeZillowUrl = (value) => {
  if (
    typeof value !== "string" ||
    value.length > 2000 ||
    [...value].some(
      (character) => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127
    ) ||
    value.includes("\\")
  ) {
    return null;
  }
  const text = value.trim();
  const candidate = /^(?:(?:www|m)\.)?zillow\.com\//i.test(text) ? `https://${text}` : text;
  const authority = candidate.match(/^https:\/\/([^/?#]+)/i)?.[1];
  if (!authority || authority.includes(":") || authority.includes("@")) return null;
  try {
    const url = new URL(candidate);
    if (
      url.protocol !== "https:" ||
      !["zillow.com", "www.zillow.com", "m.zillow.com"].includes(url.hostname) ||
      url.username ||
      url.password ||
      url.port ||
      authority.toLowerCase() !== url.hostname
    ) {
      return null;
    }
    // Validate the original path, before URL can normalize dot segments.
    const rawPath = candidate.replace(/^https:\/\/[^/?#]+/i, "").split(/[?#]/)[0];
    if (/%(?:2f|5c)/i.test(rawPath)) return null;
    const path = decodeURIComponent(rawPath);
    if (
      [...path].some((character) => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127)
    )
      return null;
    const parts = path.replace(/\/$/, "").split("/");
    if (
      parts.length < 3 ||
      parts[0] !== "" ||
      parts[1].toLowerCase() !== "homedetails" ||
      parts.slice(2).some((part) => ["", ".", ".."].includes(part))
    )
      return null;
    const match = parts[parts.length - 1].match(/^(\d+)_zpid$/i);
    const zpid = match?.[1].replace(/^0+/, "");
    return zpid && zpid.length <= 255 ? `https://www.zillow.com/homedetails/${zpid}_zpid/` : null;
  } catch {
    return null;
  }
};
